import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import {
  Create,
  ReferenceInput,
  required,
  SaveButton,
  SelectInput,
  FormDataConsumer,
  SimpleForm,
  TextInput,
  Toolbar,
  useDataProvider,
  useNotify,
  useRedirect,
  useRefresh,
} from 'react-admin';
import { CustomActions } from '../../components';
import { useTheme } from '@mui/material/styles';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const CustomToolbar = ({ checklist }) => {
  const redirect = useRedirect(); // Hook to redirect to another page
  const theme = useTheme(); // Access the theme
  const backgroundColor = theme.palette.background.paper; // Default background color

  const handleCancel = () => {
    redirect('list', 'checkpoints');
  };

  return (
    <Toolbar
      sx={{
        backgroundColor, // Set the background color
      }}
    >
      <Box display="flex" justifyContent="flex-end" width="100%">
        <Button
          variant="text"
          onClick={handleCancel}
          sx={{
            color: theme.palette.primary.main, // Optional: primary color for cancel button text
            borderColor: theme.palette.primary.main, // Optional: primary color for cancel button border
            marginRight: theme.spacing(1),
            textTransform: 'none',
            minWidth: '95px',
          }}
          size="small"
        >
          Cancel
        </Button>
        <SaveButton
          label="Create"
          size="small"
          icon={null}
          sx={{ textTransform: 'none', minWidth: '95px' }}
        />
      </Box>
    </Toolbar>
  );
};

const CheckList = ({ onChange }) => {
  const [checks, setChecks] = useState([]);
  const [checkValue, setCheckValue] = useState('');

  useEffect(() => {
    onChange(checks);
  }, [checks]);

  const handleChangeCheckInput = (event) => {
    setCheckValue(event.target.value);
  };

  const handleAddCheck = () => {
    if (checkValue.trim()) {
      const updateChecks = [...checks, { item: checkValue.trim(), order: checks.length }];
      setChecks(updateChecks);
      setCheckValue('');
    }
  };

  const handleDelete = (order) => {
    const updatedChecks = checks.filter((check) => check.order !== order);

    // Reassign order values to maintain sequence (0,1,2,...)
    const reorderedChecks = updatedChecks.map((check, index) => ({
      ...check,
      order: index,
    }));

    setChecks(reorderedChecks);
  };

  const moveUp = (orderNum) => {
    if (orderNum === 0) return; // Prevent moving the first item up
    swapOrder(orderNum, orderNum - 1);
  }

  const moveDown = (orderNum) => {
    if (orderNum === checks.length - 1) return; // Prevent moving the last item down
    swapOrder(orderNum, orderNum + 1);
  }

  // Swap the order values between two items
  const swapOrder = (order1, order2) => {
    const updatedChecks = [...checks];
    const index1 = updatedChecks.findIndex((item) => item.order === order1);
    const index2 = updatedChecks.findIndex((item) => item.order === order2);

    if (index1 !== -1 && index2 !== -1) {
      // Swap the order values
      [updatedChecks[index1].order, updatedChecks[index2].order] = [
        updatedChecks[index2].order,
        updatedChecks[index1].order,
      ];

      // Update the state with the new sorted order
      setChecks(updatedChecks.sort((a, b) => a.order - b.order));
    }
  };

  return (
    <div style={{ width: '100%' }}>
      {checks?.length > 0 &&
        checks.map((check, index, array) => (
          <div
            key={index}
            style={{
              display: 'flex',
              width: '100%',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px',
              backgroundColor: 'rgb(30, 30, 46)',
              border: '1px solid rgb(120, 120, 120)',
              marginTop: '10px',
            }}
          >
            <div>{index + 1}</div>
            <div
              style={{
                margin: 'auto',
                marginLeft: '10px',
              }}
            >
              {check.item}
            </div>
            <IconButton disabled={index === 0} onClick={() => moveUp(check.order)}>
              <KeyboardArrowUpIcon />
            </IconButton>
            <IconButton disabled={index === array.length - 1} onClick={() => moveDown(check.order)}>
              <KeyboardArrowDownIcon />
            </IconButton>
            <IconButton onClick={() => handleDelete(check.order)}>
              <DeleteIcon />
            </IconButton>
          </div>
        ))}
      <TextField
        label="Add Check"
        name="check_list"
        value={checkValue}
        onChange={handleChangeCheckInput}
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleAddCheck} edge="end">
                <AddCircleOutlineIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </div>
  );
};



const CreateCheckpoint = () => {
  const [changedChecks, setChangedChecks] = useState([]);
  const dataProvider = useDataProvider();
  const redirect = useRedirect();
  const notify = useNotify();
  const refresh = useRefresh();


  const handleCreate = async (formData) => {
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        checklist: JSON.stringify(JSON.stringify({ items: changedChecks })),
        site_id: formData.site_id,
        camera_id: formData.camera_id,
      };

      await dataProvider.create('checkpoints', { data: payload });
      notify('Checkpoint created successfully', { type: 'success' });
      refresh();
      redirect('list', 'checkpoints');
    } catch (error) {
      console.error('Error creating checkpoint:', error);
      notify('Error creating checkpoint', { type: 'error' });
    }
  }

  return (
    <Card variant="elevation" sx={{ width: '50%', overflowY: 'auto', scrollbarWidth: 'none' }}>
      <CardContent>
        <Create actions={<CustomActions title="Create Checkpoint" type="create" />}>
          <SimpleForm toolbar={<CustomToolbar checklist={changedChecks} />} onSubmit={handleCreate}>
            <Typography variant="subtitle2">Details</Typography>
            <TextInput
              label="Name"
              source="name"
              fullWidth
              validate={required('Name is required')}
            />
            <TextInput source="description" multiline minRows={5} fullWidth />
            <ReferenceInput
              source="site_id"
              reference="sites"
              label="Select a site"
            >
              <SelectInput
                optionText="name"
                fullWidth
                validate={required('Site selection is required')}
              />
            </ReferenceInput>
            <FormDataConsumer>
              {({ formData }) => formData.site_id && <ReferenceInput
                source="camera_id"
                reference="cameras"
                label="Select Camera"
                filter={{ site_id: formData.site_id }}
                perPage={100}
              >
                <SelectInput
                  optionText="name"
                  fullWidth
                  validate={[required('Camera selection is required')]}
                  sx={{
                    overflowY: 'auto !important',
                  }}
                />
              </ReferenceInput>}
            </FormDataConsumer>
            <Typography variant="subtitle2" gutterBottom>
              Checklist
            </Typography>
            <CheckList onChange={setChangedChecks} />
          </SimpleForm>
        </Create>
      </CardContent>
    </Card>
  );
};

export default CreateCheckpoint;
export { CreateCheckpoint };